import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { AccountService } from '@6grain/masofa-monolith-api-client/identity/api/account.service';
import { LoginAndPasswordViewModel } from '@6grain/masofa-monolith-api-client/identity/model/loginAndPasswordViewModel';
import { UserProfileService } from './user-profile.service';
import { LoginByOneIdCode, OneIdService } from '@6grain/masofa-monolith-api-client/identity';
import {IndexedDBCacheService} from '@app/services/indexeddb-cache.service';

export interface DecodedToken {
  unique_name?: string;
  role?: string;
  nbf?: number;
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public tokenKey = 'access_token';
  private decodedTokenKey = 'decoded_token';

  private readonly http = inject(HttpClient);
  private readonly accountService = inject(AccountService);
  private readonly oneIdService = inject(OneIdService);
  private readonly userProfileService = inject(UserProfileService);
  private readonly indexedDBCacheService = inject(IndexedDBCacheService);

  // Observable for login success events
   loginSuccess$ = new Subject<void>();

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
    const decoded = this.decodeToken(token);
    if (decoded) {
      localStorage.setItem(this.decodedTokenKey, JSON.stringify(decoded));
    }

    this.userProfileService.loadProfile(token).subscribe({
      next: (profile) => {
      },
      error: (error) => {
        console.error('Failed to load user profile after login:', error);
      }
    });

    // Emit login success event for other services to listen
    this.loginSuccess$.next();
  }

  private decodeToken(token: string): DecodedToken | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error('Invalid token format');
        return null;
      }

      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');
      const jsonPayload = atob(padded);
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey) || sessionStorage.getItem(this.tokenKey);
  }

  removeToken(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.decodedTokenKey);
    sessionStorage.removeItem(this.tokenKey);
  }

  getDecodedToken(): DecodedToken | null {
    const storedDecoded = localStorage.getItem(this.decodedTokenKey);
    if (storedDecoded) {
      try {
        return JSON.parse(storedDecoded);
      } catch {
        return null;
      }
    }
    const token = this.getToken();
    if (token) {
      const decoded = this.decodeToken(token);
      if (decoded) {
        localStorage.setItem(this.decodedTokenKey, JSON.stringify(decoded));
      }
      return decoded;
    }

    return null;
  }

  getUserRole(): string | null {
    const decoded = this.getDecodedToken();
    const role = decoded?.role || decoded?.['roles'];

    if (Array.isArray(role)) {
      return role.length > 0 ? role[0] : null;
    }

    return role || null;
  }

  getUserRoles(): string[] {
    const decoded = this.getDecodedToken();
    const role = decoded?.role || decoded?.['roles'];

    if (Array.isArray(role)) {
      return role;
    }

    if (typeof role === 'string') {
      return [role];
    }

    return [];
  }

  getUsername(): string | null {
    const decoded = this.getDecodedToken();
    return decoded?.unique_name || null;
  }

  isTokenExpired(): boolean {
    const decoded = this.getDecodedToken();
    if (!decoded || !decoded.exp) {
      return true;
    }
    const expirationDate = decoded.exp * 1000;
    return Date.now() >= expirationDate;
  }

  isAuthenticated(): boolean {
    const hasToken = !!this.getToken();
    if (!hasToken) {
      return false;
    }
    if (this.isTokenExpired()) {
      this.removeToken();
      return false;
    }

    return true;
  }

  login(username: string, password: string): Observable<any> {
    const loginData: LoginAndPasswordViewModel = {
      userName: username,
      password: password
    };

    return this.accountService.identityAccountLoginByLoginPasswordPost(loginData);
  }

  confirmTwoFactor(username: string, code: string): Observable<any> {
    return new Observable(observer => {
      observer.error({ message: 'Two-factor authentication is not currently supported by the API' });
    });
  }

  logout(): void {
    // Ensure region caches are cleared for every logout path (toolbar, guard, login flows, etc.)
    void Promise.allSettled([
      this.indexedDBCacheService.clear('regions_data_cache'),
      this.indexedDBCacheService.clear('region_maps_cache'),
      this.indexedDBCacheService.clear('region_maps_last_update_at'),
    ]);

    this.removeToken();
    const keysToRemove = Object.keys(localStorage).filter(key =>
      key.includes('authnResult') ||
      key.includes('oidc.user') ||
      key.includes('_token')
    );
    keysToRemove.forEach(key => localStorage.removeItem(key));

    sessionStorage.clear();

    this.userProfileService.clearProfile();
  }

  hasRole(role: string): boolean {
    const userRoles = this.getUserRoles();
    return userRoles.includes(role);
  }

  isAdmin(): boolean {
    return this.hasRole('Admin');
  }

  hasAllowedRole(): boolean {
    const allowedRoles = ['Admin', 'SystemAdmin', 'Operator', 'ModuleAdmin', 'User', 'Manager', 'Application', 'Subscriber', 'Foreman', 'FieldWorker'];
    const userRoles = this.getUserRoles();

    return userRoles.some(userRole => allowedRoles.includes(userRole));
  }

  getOneIdRedirectUrl(): Observable<any> {
    return this.oneIdService.identityOneIdGetRedirectUrlGet();
  }

  getTokenByOneId(code: string): Observable<any> {
    const loginData: LoginByOneIdCode = {
      code: code
    };

    return this.oneIdService.identityOneIdGetUserTokenPost(loginData);
  }
}
